import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";

import type { CryptoSelectOption } from "../../presentation/CryptoSelect";
import {
  ASSET_ADDRESS_DEFAULTS,
  type AssetAddressFormValues,
  assetAddressSchema,
  refineAssetAddress,
} from "./schema";
import {
  canKeepAssetOnChain,
  useAddressValidation,
} from "./useAddressValidation";

interface UseAssetAddressFormProps {
  onNext: (data: AssetAddressFormValues) => void;
  initialValues?: Partial<AssetAddressFormValues>;
  assetOptionsByChain: Record<string, CryptoSelectOption[]>;
  chainNameByCode: Record<string, string>;
}

export const useAssetAddressForm = ({
  onNext,
  initialValues,
  assetOptionsByChain,
  chainNameByCode,
}: UseAssetAddressFormProps) => {
  const {
    control,
    handleSubmit,
    setValue,
    trigger,
    watch,
    formState: { isValid },
  } = useForm<AssetAddressFormValues>({
    resolver: zodResolver(
      assetAddressSchema.superRefine((data, ctx) =>
        refineAssetAddress(data, ctx, assetOptionsByChain),
      ),
    ),
    mode: "onChange",
    defaultValues: { ...ASSET_ADDRESS_DEFAULTS, ...initialValues },
  });

  const address = watch("address");
  const chain = watch("chain");
  const asset = watch("asset");
  const assetOptions = chain ? (assetOptionsByChain[chain] ?? []) : [];
  const isAssetDisabled = !chain;

  const addressValidation = useAddressValidation({
    address,
    chain,
    asset,
    chainNameByCode,
    assetOptionsByChain,
  });

  const onChainChange = (chain: string) => {
    setValue("chain", chain, { shouldValidate: true, shouldDirty: true });

    if (asset && !canKeepAssetOnChain(asset, chain, assetOptionsByChain)) {
      setValue("asset", "", { shouldDirty: true });
    }

    if (address) {
      trigger(["address", "chain"]);
    }
  };

  const onAssetChange = (asset: string) => {
    setValue("asset", asset, { shouldValidate: true, shouldDirty: true });
  };

  const onAddressChange = (value: string) => {
    setValue("address", value, { shouldValidate: true, shouldDirty: true });
    if (chain) {
      trigger("chain");
    }
  };

  const onSwitchToMatchedChain = () => {
    if (addressValidation.status !== "mismatch") {
      return;
    }

    const { matchedChainCode, canKeepAsset } = addressValidation;

    setValue("chain", matchedChainCode, {
      shouldValidate: true,
      shouldDirty: true,
    });

    if (!canKeepAsset) {
      setValue("asset", "", { shouldDirty: true });
    }
    if (address) {
      trigger(["address", "chain"]);
    }
  };

  const isSubmitDisabled = !chain || !asset || !address || !isValid;

  const onSubmit = handleSubmit((data) => onNext(data));

  return {
    control,
    onSubmit,
    isSubmitDisabled,
    onChainChange,
    onAssetChange,
    onAddressChange,
    onSwitchToMatchedChain,
    assetOptions,
    isAssetDisabled,
    addressValidation,
  };
};

export type UseAssetAddressFormReturn = ReturnType<typeof useAssetAddressForm>;
